import { useContext } from "react";

import { createReference } from "@medplum/core";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { rem } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { Loading } from "@/components/Loading";
import { EncounterContext } from "@/contexts/encounter";
import { PatientContext } from "@/contexts/patient";
import { PhysicalExamTypes } from "@/fhir/enums/physical-exams";
import { PhysicalExamQuestionnaires } from "@/fhir/enums/questionnaires";

import { useTemporarySubmit } from "../../utils/hooks/use-temporary-submit";
import usePhysicalExamInitialValues from "../hooks/use-physical-exam-initial-values";
import usePhysicalExamQuestionnaire from "../hooks/use-physical-exam-questionnaire";
import usePhysicalExamUnsavedChanges from "../hooks/usePhysicalExamUnsavedChanges";
import PhysicalExamHeader from "../PhysicalExamHeader";
import PhysicalExamList from "../PhysicalExamList";
import {
  amendPhysicalExamQuestionnaireResponse,
  createPhysicalExamQuestionnaireResponse,
  getMainQuestionnaireUrlFromEncounter,
} from "../services/physical-exams";
import { MUSCULOSKELETAL_FORM_INITIAL_VALUES } from "../types/musculoskeletal";
import { PhysicalExamProps } from "../types/physical-exams";
import { PhysicalExamForm, PhysicalExamFormSchema } from "../zod-schemas/physical-exams";

export default function PhysicalExamMusculoskeletal({
  url,
  submitTrigger,
  editable,
  isAmendment,
}: PhysicalExamProps) {
  const { patient } = useContext(PatientContext);
  const { encounter } = useContext(EncounterContext);
  const { sections, questionnaire } = usePhysicalExamQuestionnaire(
    PhysicalExamQuestionnaires.MusculoskeletalPhysicalExam,
  );
  const medplum = useMedplum();
  const form = useForm<PhysicalExamForm>({
    initialValues: MUSCULOSKELETAL_FORM_INITIAL_VALUES,
    validate: zodResolver(PhysicalExamFormSchema),
  });

  const formSetValues = form.setValues;

  const [initialValues, questionnaireResponse, isLoading] = usePhysicalExamInitialValues(
    questionnaire?.id,
    encounter,
    formSetValues,
    MUSCULOSKELETAL_FORM_INITIAL_VALUES,
  );
  const { markAsSaved } = usePhysicalExamUnsavedChanges(url, form, initialValues);
  const practitioner = medplum.getProfile() as Practitioner;

  const handleSubmit = async (values: PhysicalExamForm) => {
    const mainQuestionnaireUrl = getMainQuestionnaireUrlFromEncounter(encounter!);
    // If questionnaireResponse is undefined, then the amend action shouldn't happen
    // since the PE answers are being created at this point.
    if (isAmendment && questionnaireResponse) {
      await amendPhysicalExamQuestionnaireResponse(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    } else {
      await createPhysicalExamQuestionnaireResponse(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    }
    markAsSaved();
  };

  useTemporarySubmit<PhysicalExamForm>(form, submitTrigger, handleSubmit);

  if (!patient?.id || isLoading) return <Loading height={rem(96)} />;

  return (
    <form
      data-testid="physical-exam-musculoskeletal-form"
      onSubmit={form.onSubmit((values, _) => handleSubmit(values))}
    >
      <PhysicalExamHeader
        url={url}
        form={form}
        sections={sections}
        canDelete={editable}
        title="Musculoskeletal"
        clientId={patient!.id!}
        showQuickSelection={editable}
        value={PhysicalExamTypes.Musculoskeletal}
      >
        <PhysicalExamList form={form} sections={sections} editable={editable} />
      </PhysicalExamHeader>
    </form>
  );
}
